import type { SdkConfigApiResponse } from './embeddable.model';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { OrgContextProvider } from '@provider-directory/components/org-context/org-context-provider';
import { FlagProvider } from '@provider-directory/contexts/FlagContext';
import { getFabricSessionId } from '@provider-directory/lib/fabricSid';
import { ThemeConfig } from '@provider-directory/types';
import { fetchSDKOrgContext } from './embeddable.model';
import { WidgetEventEmitter } from './widget-event-emitter';

interface EmbeddableSearchProviderContextProps {
  widgetId?: string;
  googleApiKey: string;
}
export interface EmbeddableSearchProviderContextValue
  extends EmbeddableSearchProviderContextProps {
  directoryUrl?: string;
  fabricSid: string | null;
  themeConfig?: Partial<ThemeConfig>;
  eventEmitter: WidgetEventEmitter | null;
}

const EmbeddableSearchProviderContext =
  createContext<EmbeddableSearchProviderContextValue | null>(null);

export const useEmbeddableSearchProviderContext =
  (): EmbeddableSearchProviderContextValue | null => {
    const context = useContext(EmbeddableSearchProviderContext);
    return context ?? null;
  };

export const EmbeddableSearchProvider: React.FC<
  EmbeddableSearchProviderContextProps & { children: ReactNode }
> = ({ widgetId, googleApiKey, children }) => {
  const client = useMemo(() => new QueryClient(), []);
  const [fabricSid, setFabricSid] = useState<string | null>(null);
  const [orgContext, setOrgContext] = useState<SdkConfigApiResponse>();
  const [directoryUrl, setDirectoryUrl] = useState<string | undefined>(
    undefined,
  );

  // Create event emitter only if in event mode (has widgetId)
  const eventEmitter = useMemo(() => {
    return widgetId ? new WidgetEventEmitter(widgetId) : null;
  }, [widgetId]);

  // Generate or retrieve fabric_sid for cross-domain tracking after mount
  useEffect(() => {
    setFabricSid(getFabricSessionId());
  }, []);

  // Fetch config from the app's endpoint
  useEffect(() => {
    const init = async () => {
      const fetchedOrgContext = await fetchSDKOrgContext();
      if (fetchedOrgContext) {
        setOrgContext(fetchedOrgContext);
        // directoryUrl gets set by the useEffect below
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!orgContext) return;

    const directoryUrlToTest = orgContext.config.sdk?.directoryUrl;
    if (!directoryUrlToTest) return;

    // Check if directoryUrl is valid by hitting the health endpoint
    const testDirectoryUrl = async () => {
      const warnMessage = `Invalid directory URL: ${directoryUrlToTest}. Reverting to local API...`;
      try {
        const response = await fetch(`${directoryUrlToTest}/-/health`);
        if (response.ok) {
          setDirectoryUrl(directoryUrlToTest);
        } else {
          console.warn(warnMessage);
        }
      } catch {
        console.warn(warnMessage);
      }
    };

    testDirectoryUrl();
  }, [orgContext]);

  if (!orgContext) return null;

  return (
    <QueryClientProvider client={client}>
      <FlagProvider flags={orgContext.featureFlags}>
        <OrgContextProvider
          orgKey={orgContext.key}
          runtimeConfig={orgContext.runtimeConfig}
        >
          <EmbeddableSearchProviderContext.Provider
            value={{
              widgetId,
              fabricSid,
              googleApiKey,
              eventEmitter,
              directoryUrl,
              themeConfig: orgContext.config.sdk?.theme,
            }}
          >
            {children}
          </EmbeddableSearchProviderContext.Provider>
        </OrgContextProvider>
      </FlagProvider>
    </QueryClientProvider>
  );
};
